import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import {
  ChartLineUp,
  Lightning,
  WhatsappLogo,
  Users,
  Plus,
  ChatCircleDots,
  TrendUp,
  Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="border border-[#E5E7EB] bg-white p-5 hover:bg-[#F8F9FA] transition-colors duration-150"
    >
      <div className="flex items-center justify-between">
        <div className="label-mono">{label}</div>
        <Icon size={18} weight="duotone" className={accent ? "text-[#002FA7]" : "text-[#64748B]"} />
      </div>
      <div className="mt-3 font-display font-black text-4xl tracking-tight text-[#0F172A]">
        {value}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [advanced, setAdvanced] = useState(null);
  const [waStatus, setWaStatus] = useState({ connected: false, service_up: false });
  const [recent, setRecent] = useState([]);

  // Manual test form
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [sendWa, setSendWa] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastTest, setLastTest] = useState(null);

  const load = async () => {
    try {
      const [s, w, t, a] = await Promise.all([
        api.get("/tests/stats"),
        api.get("/whatsapp/status"),
        api.get("/tests?limit=5"),
        api.get("/stats/advanced"),
      ]);
      setStats(s.data);
      setWaStatus(w.data);
      setRecent(t.data);
      setAdvanced(a.data);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const createTest = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Informe o nome do cliente");
    setSubmitting(true);
    try {
      const { data } = await api.post("/tests/manual", {
        client_name: name.trim(),
        phone_number: phone.trim() || null,
        send_via_whatsapp: sendWa,
      });
      setLastTest(data);
      toast.success(`Teste criado: ${data.username}`);
      setName("");
      setPhone("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao criar teste");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout
      title="Visão geral"
      subtitle="Dashboard / Control Room"
      action={
        <button
          data-testid="header-whatsapp-btn"
          onClick={() => nav("/whatsapp")}
          className="flex items-center gap-2 border border-[#E5E7EB] px-4 py-2 text-sm hover:bg-[#F8F9FA] transition-colors duration-150"
        >
          <span
            className={`h-2 w-2 rounded-full ${
              waStatus.connected ? "bg-[#10B981]" : "bg-[#EF4444]"
            }`}
          />
          WhatsApp {waStatus.connected ? "conectado" : "desconectado"}
        </button>
      }
    >
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total de Testes" value={stats?.total ?? "—"} icon={ChartLineUp} accent />
        <StatCard label="Hoje" value={stats?.today ?? "—"} icon={Lightning} />
        <StatCard label="Últimos 7d" value={stats?.week ?? "—"} icon={ChartLineUp} />
        <StatCard label="Via WhatsApp" value={stats?.from_whatsapp ?? "—"} icon={WhatsappLogo} />
      </section>

      <section className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-[#E5E7EB] bg-white">
          <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
            <div>
              <div className="label-mono">Atividade recente</div>
              <div className="font-display font-bold text-lg mt-1">Últimos testes</div>
            </div>
            <button
              data-testid="view-all-tests-btn"
              onClick={() => nav("/history")}
              className="text-xs text-[#002FA7] hover:underline"
            >
              Ver todos →
            </button>
          </div>
          <div className="divide-y divide-[#E5E7EB]">
            {recent.length === 0 && (
              <div className="px-5 py-10 text-center text-sm text-[#64748B]">
                Nenhum teste gerado ainda.
              </div>
            )}
            {recent.map((t) => (
              <div
                key={t.id}
                data-testid={`recent-test-${t.id}`}
                className="px-5 py-3 flex items-center justify-between hover:bg-[#F8F9FA] transition-colors duration-150"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#0F172A] mono">
                    {t.username}
                  </div>
                  <div className="text-xs text-[#64748B] mt-0.5">
                    {t.client_name} · {t.phone_number || "sem número"} ·{" "}
                    {new Date(t.created_at).toLocaleString("pt-BR")}
                  </div>
                </div>
                <span
                  className={`label-mono px-2 py-1 ${
                    t.source === "whatsapp"
                      ? "bg-[#10B981]/10 text-[#10B981]"
                      : "bg-[#002FA7]/10 text-[#002FA7]"
                  }`}
                >
                  {t.source}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-[#E5E7EB] bg-white">
          <div className="px-5 py-4 border-b border-[#E5E7EB]">
            <div className="label-mono">Ação rápida</div>
            <div className="font-display font-bold text-lg mt-1 flex items-center gap-2">
              <Plus size={20} weight="bold" /> Criar teste manual
            </div>
          </div>
          <form onSubmit={createTest} className="p-5 space-y-4">
            <div>
              <label className="label-mono block mb-2">Nome do cliente</label>
              <input
                data-testid="manual-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7]"
                placeholder="pedro"
              />
            </div>
            <div>
              <label className="label-mono block mb-2">WhatsApp (opcional)</label>
              <input
                data-testid="manual-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7]"
                placeholder="5511999999932"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-[#0F172A]">
              <input
                data-testid="manual-send-wa-check"
                type="checkbox"
                checked={sendWa}
                onChange={(e) => setSendWa(e.target.checked)}
                className="h-4 w-4 border border-[#E5E7EB] rounded-sm"
              />
              Enviar via WhatsApp automaticamente
            </label>
            <button
              data-testid="manual-create-btn"
              type="submit"
              disabled={submitting}
              className="w-full bg-[#002FA7] text-white text-sm font-medium py-2.5 rounded-sm hover:bg-[#001f7a] transition-colors duration-150 disabled:opacity-50"
            >
              {submitting ? "Gerando..." : "Gerar teste"}
            </button>

            {lastTest && (
              <div data-testid="last-test-result" className="mt-3 border border-[#E5E7EB] p-3 bg-[#F8F9FA]">
                <div className="label-mono mb-1">Último teste criado</div>
                <div className="mono text-sm">
                  <div>user: <span className="kbd">{lastTest.username}</span></div>
                  <div className="mt-1">pass: <span className="kbd">{lastTest.password}</span></div>
                </div>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Stats avançadas */}
      {advanced && (
        <section className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-4">
          <StatCard
            label="Mensagens 7d"
            value={advanced.msgs_in_7d ?? 0}
            icon={ChatCircleDots}
          />
          <StatCard
            label="Testes 7d"
            value={advanced.trials_7d ?? 0}
            icon={Lightning}
          />
          <StatCard
            label="Conversão"
            value={`${advanced.conversion_rate_7d ?? 0}%`}
            icon={TrendUp}
            accent
          />
          <div className="border border-[#E5E7EB] bg-white p-5">
            <div className="flex items-center justify-between">
              <div className="label-mono">WhatsApp</div>
              <WhatsappLogo size={18} weight="duotone"
                className={advanced.whatsapp_connected ? "text-[#10B981]" : "text-[#EF4444]"} />
            </div>
            <div className="mt-3 font-display font-black text-2xl tracking-tight">
              {advanced.whatsapp_connected ? "Conectado" : "Offline"}
            </div>
          </div>
        </section>
      )}

      {/* Top contatos + horários pico */}
      {advanced && (advanced.top_contacts?.length > 0 || advanced.peak_hours?.length > 0) && (
        <section className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Top contatos */}
          <div className="border border-[#E5E7EB] bg-white">
            <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
              <div>
                <div className="label-mono">CRM</div>
                <div className="font-display font-bold text-lg mt-1 flex items-center gap-2">
                  <Users size={18} weight="duotone" /> Top 10 contatos
                </div>
              </div>
              <button onClick={() => nav("/contacts")} className="text-xs text-[#002FA7] hover:underline">
                Ver todos →
              </button>
            </div>
            <div className="divide-y divide-[#E5E7EB]">
              {advanced.top_contacts.slice(0, 10).map((c, i) => (
                <div key={c.id} className="px-5 py-2.5 flex items-center justify-between hover:bg-[#F8F9FA]">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="label-mono w-6 text-right">{i + 1}.</span>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{c.name || c.push_name || c.phone_number}</div>
                      <div className="text-xs text-[#64748B] font-mono">{c.phone_number}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span title="Mensagens"><ChatCircleDots size={12} className="inline mr-1" />{c.total_messages}</span>
                    <span title="Testes" className="text-[#16A34A]">⚡{c.total_trials}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Horários de pico */}
          <div className="border border-[#E5E7EB] bg-white">
            <div className="px-5 py-4 border-b border-[#E5E7EB]">
              <div className="label-mono">Análise temporal</div>
              <div className="font-display font-bold text-lg mt-1 flex items-center gap-2">
                <Clock size={18} weight="duotone" /> Horários de pico (14d)
              </div>
            </div>
            <div className="p-5">
              {advanced.peak_hours.length === 0 ? (
                <div className="text-sm text-[#64748B] text-center py-6">Sem dados ainda</div>
              ) : (
                <PeakHoursChart data={advanced.peak_hours} />
              )}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}

function PeakHoursChart({ data }) {
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="flex items-end gap-1 h-32">
      {Array.from({ length: 24 }).map((_, hour) => {
        const item = data.find((d) => parseInt(d.hour) === hour);
        const count = item?.count || 0;
        const height = (count / max) * 100;
        return (
          <div key={hour} className="flex-1 flex flex-col items-center justify-end gap-1">
            <div className="text-[9px] text-[#64748B]">{count > 0 ? count : ""}</div>
            <div
              className="w-full bg-[#002FA7] hover:bg-[#001f7a] transition-colors min-h-[2px]"
              style={{ height: `${height}%` }}
              title={`${hour}h - ${count} msgs`}
            />
            <div className="text-[9px] text-[#64748B]">{hour}</div>
          </div>
        );
      })}
    </div>
  );
}
